// SPDX-License-Identifier: AGPL-3.0-or-later

import {
	SettingsTabContainer,
	SettingsTabContent,
	SettingsTabSection,
} from '@app/features/app/components/dialogs/shared/SettingsTabLayout';
import {Button} from '@app/features/ui/button/Button';
import * as ToastCommands from '@app/features/ui/commands/ToastCommands';
import {Tooltip} from '@app/features/ui/tooltip/Tooltip';
import styles from '@app/features/user/components/modals/tabs/component_gallery_tab/OverlaysTab.module.css';
import {Trans, useLingui} from '@lingui/react/macro';
import {DotsThreeOutlineIcon} from '@phosphor-icons/react';
import {observer} from 'mobx-react-lite';
import type React from 'react';

interface OverlaysTabProps {
	openContextMenu: (event: React.MouseEvent<HTMLElement>) => void;
}
export const OverlaysTab: React.FC<OverlaysTabProps> = observer(({openContextMenu}) => {
	const {t} = useLingui();
	return (
		<SettingsTabContainer>
			<SettingsTabContent>
				<SettingsTabSection
					title={<Trans>Tooltips</Trans>}
					description={<Trans>Hover over buttons to see tooltips in different positions.</Trans>}
				>
					<div className={styles.buttonsWrapper}>
						<Tooltip text={t`I am a tooltip`}>
							<Button>
								<Trans>Hover Me</Trans>
							</Button>
						</Tooltip>
						<Tooltip text={t`Top Tooltip`} position="top">
							<Button variant="secondary">
								<Trans>Top</Trans>
							</Button>
						</Tooltip>
						<Tooltip text={t`Right Tooltip`} position="right">
							<Button variant="secondary">
								<Trans>Right</Trans>
							</Button>
						</Tooltip>
						<Tooltip text={t`Bottom Tooltip`} position="bottom">
							<Button variant="secondary">
								<Trans>Bottom</Trans>
							</Button>
						</Tooltip>
						<Tooltip text={t`Left Tooltip`} position="left">
							<Button variant="secondary">
								<Trans>Left</Trans>
							</Button>
						</Tooltip>
					</div>
				</SettingsTabSection>
				<SettingsTabSection
					title={<Trans>Toasts</Trans>}
					description={<Trans>Toasts appear in the top-center of the screen.</Trans>}
				>
					<div className={styles.buttonsWrapper}>
						<Button onClick={() => ToastCommands.createToast({type: 'success', children: t`Great Success!`})}>
							<Trans>Success</Trans>
						</Button>
						<Button
							variant="danger-primary"
							onClick={() => ToastCommands.createToast({type: 'error', children: t`Something went wrong.`})}
						>
							<Trans>Error</Trans>
						</Button>
					</div>
				</SettingsTabSection>
				<SettingsTabSection
					title={<Trans>Context Menus</Trans>}
					description={
						<Trans>
							Context menus can be opened with left-click (on buttons) or right-click (on other elements). This
							demonstrates various menu items including checkboxes, radio buttons, sliders, and submenus.
						</Trans>
					}
				>
					<div className={styles.buttonsWrapper}>
						<Button leftIcon={<DotsThreeOutlineIcon size={16} />} onClick={openContextMenu}>
							<Trans>Open Menu</Trans>
						</Button>
						<Button
							square
							icon={<DotsThreeOutlineIcon size={16} />}
							aria-label={t`Open Menu (icon)`}
							onClick={openContextMenu}
						/>
						<div role="button" tabIndex={0} onContextMenu={openContextMenu} className={styles.demoArea}>
							<Trans>Right-click here to open the context menu</Trans>
						</div>
					</div>
				</SettingsTabSection>
			</SettingsTabContent>
		</SettingsTabContainer>
	);
});
